/**
 * Frontend logging utility
 * Provides structured logging with different levels and can be easily configured
 * for production vs development environments.
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  data?: any;
  context?: string;
}

class Logger {
  private level: LogLevel;
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
    this.level = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;
  }

  private formatMessage(level: LogLevel, message: string, data?: any, context?: string): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level: LogLevel[level],
      message,
      data,
      context,
    };
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.level;
  }

  private output(entry: LogEntry): void {
    if (!this.isDevelopment) {
      // In production, you might want to send logs to a service
      // For now, we'll just use console.warn and console.error for important messages
      if (entry.level === 'WARN') {
        console.warn(entry.message, entry.data || '');
      } else if (entry.level === 'ERROR') {
        console.error(entry.message, entry.data || '');
      }
      return;
    }

    // Development logging with colors
    const colors = {
      DEBUG: 'color: #666',
      INFO: 'color: #2563eb',
      WARN: 'color: #d97706',
      ERROR: 'color: #dc2626',
    };

    const style = colors[entry.level as keyof typeof colors] || '';
    const contextStr = entry.context ? `[${entry.context}] ` : '';
    
    console.log(
      `%c${entry.level} %c${contextStr}${entry.message}`,
      `${style}; font-weight: bold`,
      'color: inherit',
      entry.data || ''
    );
  }

  debug(message: string, data?: any, context?: string): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      this.output(this.formatMessage(LogLevel.DEBUG, message, data, context));
    }
  }

  info(message: string, data?: any, context?: string): void {
    if (this.shouldLog(LogLevel.INFO)) {
      this.output(this.formatMessage(LogLevel.INFO, message, data, context));
    }
  }

  warn(message: string, data?: any, context?: string): void {
    if (this.shouldLog(LogLevel.WARN)) {
      this.output(this.formatMessage(LogLevel.WARN, message, data, context));
    }
  }

  error(message: string, data?: any, context?: string): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      this.output(this.formatMessage(LogLevel.ERROR, message, data, context));
    }
  }

  setLevel(level: LogLevel): void {
    this.level = level;
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience functions
export const log = {
  debug: (message: string, data?: any, context?: string) => logger.debug(message, data, context),
  info: (message: string, data?: any, context?: string) => logger.info(message, data, context),
  warn: (message: string, data?: any, context?: string) => logger.warn(message, data, context),
  error: (message: string, data?: any, context?: string) => logger.error(message, data, context),
};
